import api from "./api";
import { getApiUrl } from "./config";
import { handleApiCall } from "../utils/apiResponseHandler";
import { endpoint } from "./endpoints";

// ============ TYPES ============

export interface LeadVendor {
  id: string;
  name: string;
  companyName?: string;
  contactEmail: string;
  contactPhone?: string;
  websiteUrl?: string;
  apiKeyPrefix: string;
  apiKeyCreatedAt: string;
  mfaEnabled: boolean;
  mfaSetupCompletedAt?: string;
  rateLimitPerMinute: number;
  allowedIps?: string[];
  isActive: boolean;
  fieldMapping?: Record<string, string>;
  lastApiCallAt?: string;
  totalLeadsSubmitted: number;
  totalLeadsSuccess: number;
  totalLeadsFailed: number;
  notes?: string;
  createdByUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VendorSubmission {
  id: string;
  vendorId: string;
  externalLeadId?: string;
  agentEmail: string;
  status: 'pending' | 'success' | 'failed' | 'no_match';
  errorMessage?: string;
  errorCode?: string;
  createdLeadId?: string;
  matchedUserId?: string;
  retryCount: number;
  lastRetryAt?: string;
  requestIp?: string;
  createdAt: string;
  processedAt?: string;
}

export interface VendorApiLog {
  id: string;
  vendorId?: string;
  endpoint: string;
  method: string;
  requestIp?: string;
  responseStatus?: number;
  responseTimeMs?: number;
  leadsCount?: number;
  successCount?: number;
  failedCount?: number;
  createdAt: string;
}

export interface TopAgent {
  agentEmail: string;
  agentName?: string;
  totalLeads: number;
  successfulLeads: number;
  failedLeads: number;
  lastPurchaseAt: string;
}

export interface BatchSubmission {
  id: string;
  createdAt: string;
  leadsCount: number;
  successCount: number;
  failedCount: number;
  responseTimeMs?: number;
  requestIp?: string;
  submissions?: {
    id: string;
    agentEmail: string;
    externalLeadId?: string;
    status: string;
    errorMessage?: string;
  }[];
}

export interface VendorStats {
  totalLeadsSubmitted: number;
  totalLeadsSuccess: number;
  totalLeadsFailed: number;
  totalLeadsNoMatch: number;
  last30Days: {
    submitted: number;
    success: number;
    failed: number;
    noMatch: number;
  };
}

export interface SecurityAlert {
  type: 'suspicious_ip' | 'rate_limit_exceeded' | 'invalid_api_key_attempts' | 'mfa_failure' | 'ip_change';
  message: string;
  details: Record<string, any>;
  createdAt: string;
}

export interface CreateVendorParams {
  name: string;
  companyName?: string;
  contactEmail: string;
  contactPhone?: string;
  websiteUrl?: string;
  rateLimitPerMinute?: number;
  allowedIps?: string[];
  notes?: string;
}

export interface UpdateVendorParams {
  name?: string;
  companyName?: string;
  contactEmail?: string;
  contactPhone?: string;
  websiteUrl?: string;
  rateLimitPerMinute?: number;
  allowedIps?: string[];
  notes?: string;
  isActive?: boolean;
  fieldMapping?: Record<string, string>;
}

export interface SendPromotionParams {
  title: string;
  message: string;
  linkUrl?: string;
}

export interface PromotionStatus {
  vendorId: string;
  vendorName: string;
  canSendPromotion: boolean;
  nextAvailableAt: string | null;
  lastPromotionSentAt: string | null;
  clientCount: number;
}

// Vendor Portal Auth Types
export interface VendorLoginResponse {
  requiresMfaSetup: boolean;
  requiresMfaCode: boolean;
  vendorId?: string;
  vendorName?: string;
}

export interface VendorMfaSetupResponse {
  qrCodeUrl: string;
  manualKey: string;
  backupCodes: string[];
}

export interface VendorSessionResponse {
  token: string;
  expiresAt: string;
  vendorId: string;
  vendorName: string;
}

// ============ ADMIN SERVICE FUNCTIONS ============

class VendorAdminService {
  /**
   * List all vendors (admin only)
   */
  async listVendors(params?: {
    isActive?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ vendors: LeadVendor[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.isActive !== undefined) queryParams.append("isActive", String(params.isActive));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(`${endpoint.vendor.admin.list}${queryString ? `?${queryString}` : ""}`);

    const result = await handleApiCall<{ vendors: LeadVendor[]; total: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { vendors: [], total: 0 };
  }

  /**
   * Create a new vendor (returns API key only once)
   */
  async createVendor(params: CreateVendorParams): Promise<{
    vendor: LeadVendor;
    apiKey: string;
    loginUrl: string;
    setupInstructions: string[];
  }> {
    const url = getApiUrl(endpoint.vendor.admin.create);

    return handleApiCall(async () => {
      return api.post({ url, body: params, auth: true });
    });
  }

  /**
   * Get vendor details with security alerts
   */
  async getVendor(id: string): Promise<{
    vendor: LeadVendor;
    stats: VendorStats;
    securityAlerts: SecurityAlert[];
  }> {
    const url = getApiUrl(endpoint.vendor.admin.get(id));

    return handleApiCall(async () => {
      return api.get({ url, auth: true });
    });
  }

  /**
   * Update vendor settings
   */
  async updateVendor(id: string, params: UpdateVendorParams): Promise<{ vendor: LeadVendor }> {
    const url = getApiUrl(endpoint.vendor.admin.update(id));

    return handleApiCall(async () => {
      return api.patch({ url, body: params, auth: true });
    });
  }

  /**
   * Delete (soft delete) a vendor
   */
  async deleteVendor(id: string): Promise<void> {
    const url = getApiUrl(endpoint.vendor.admin.delete(id));

    return handleApiCall(async () => {
      return api.delete({ url, auth: true });
    });
  }

  /**
   * Regenerate API key for a vendor (returns new key only once)
   */
  async regenerateApiKey(id: string): Promise<{
    vendor: LeadVendor;
    apiKey: string;
    warning: string;
  }> {
    const url = getApiUrl(endpoint.vendor.admin.regenerateKey(id));

    return handleApiCall(async () => {
      return api.post({ url, body: {}, auth: true });
    });
  }

  /**
   * Get vendor's submissions
   */
  async getVendorSubmissions(
    id: string,
    params?: { status?: string; limit?: number; offset?: number }
  ): Promise<{ submissions: VendorSubmission[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(`${endpoint.vendor.admin.getSubmissions(id)}${queryString ? `?${queryString}` : ""}`);

    const result = await handleApiCall<{ submissions: VendorSubmission[]; total: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { submissions: [], total: 0 };
  }

  /**
   * Get vendor's API logs
   */
  async getVendorLogs(
    id: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ logs: VendorApiLog[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const url = getApiUrl(`${endpoint.vendor.admin.getLogs(id)}${queryString ? `?${queryString}` : ""}`);

    const result = await handleApiCall<{ logs: VendorApiLog[]; total: number }>(async () => {
      return api.get({ url, auth: true });
    });
    return result || { logs: [], total: 0 };
  }

  /**
   * Get all security alerts across vendors
   */
  async getSecurityAlerts(): Promise<{
    vendorAlerts: { vendorId: string; alerts: SecurityAlert[] }[];
    failedAttempts: { key: string; count: number; lastAttempt: string; ips: string[] }[];
    summary: { totalVendorsWithAlerts: number; totalFailedAttemptKeys: number };
  }> {
    const url = getApiUrl(endpoint.vendor.admin.securityAlerts);

    return handleApiCall(async () => {
      return api.get({ url, auth: true });
    });
  }

  /**
   * Send a promotional notification from a vendor to their clients.
   * Rate limited to once per week.
   */
  async sendPromotion(
    vendorId: string,
    params: SendPromotionParams
  ): Promise<{ notificationsSent: number; message: string }> {
    const url = getApiUrl(endpoint.vendor.admin.sendPromotion(vendorId));

    return handleApiCall(async () => {
      return api.post({ url, body: params, auth: true });
    });
  }

  /**
   * Get promotion status for a vendor (rate limit info, client count)
   */
  async getPromotionStatus(vendorId: string): Promise<PromotionStatus> {
    const url = getApiUrl(endpoint.vendor.admin.getPromotionStatus(vendorId));

    const result = await handleApiCall<{ data: PromotionStatus }>(async () => {
      return api.get({ url, auth: true });
    });
    return result.data;
  }
}

// ============ VENDOR PORTAL SERVICE FUNCTIONS ============

class VendorPortalService {
  private sessionToken: string | null = null;

  /**
   * Set session token for authenticated requests
   * Uses sessionStorage so token is cleared when browser/tab is closed
   */
  setSessionToken(token: string) {
    this.sessionToken = token;
    // Use sessionStorage (not localStorage) so session expires on tab/browser close
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('vendor_session_token', token);
    }
  }

  /**
   * Get stored session token
   */
  getSessionToken(): string | null {
    if (this.sessionToken) return this.sessionToken;
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('vendor_session_token');
    }
    return null;
  }

  /**
   * Clear session token
   */
  clearSessionToken() {
    this.sessionToken = null;
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('vendor_session_token');
    }
  }

  /**
   * Make authenticated request with vendor session token
   */
  private async makeAuthenticatedRequest<T>(
    method: 'get' | 'post',
    urlPath: string,
    body?: any
  ): Promise<T> {
    const token = this.getSessionToken();
    if (!token) {
      // Clear any stale session data
      this.clearSessionToken();
      const error = new Error('No session token. Please log in.');
      throw error;
    }

    const url = getApiUrl(urlPath);
    const options = { headers: { 'X-Vendor-Session': token } };

    try {
      return await handleApiCall<T>(async () => {
        if (method === 'get') {
          return api.get({ url, auth: false, options });
        } else {
          return api.post({ url, body, auth: false, options });
        }
      });
    } catch (error: any) {
      // If backend returns 401 or session-related error, clear token and re-throw
      const errorMessage = error?.message?.toLowerCase() || '';
      if (error?.code === 401 ||
          errorMessage.includes('session') ||
          errorMessage.includes('unauthorized') ||
          errorMessage.includes('token')) {
        this.clearSessionToken();
      }
      throw error;
    }
  }

  /**
   * Step 1: Validate API key and get MFA status
   */
  async login(apiKey: string): Promise<VendorLoginResponse> {
    const url = getApiUrl(endpoint.vendor.auth.login);

    return handleApiCall(async () => {
      return api.post({ url, body: { apiKey }, auth: false });
    });
  }

  /**
   * Step 2a: Set up MFA (first-time login)
   */
  async setupMfa(apiKey: string): Promise<VendorMfaSetupResponse> {
    const url = getApiUrl(endpoint.vendor.auth.mfaSetup);

    return handleApiCall(async () => {
      return api.post({ url, body: { apiKey }, auth: false });
    });
  }

  /**
   * Step 2b: Verify MFA code and enable MFA
   */
  async verifyMfa(apiKey: string, code: string): Promise<VendorSessionResponse> {
    const url = getApiUrl(endpoint.vendor.auth.mfaVerify);

    const data = await handleApiCall<VendorSessionResponse>(async () => {
      return api.post({ url, body: { apiKey, code }, auth: false });
    });
    if (data?.token) {
      this.setSessionToken(data.token);
    }
    return data;
  }

  /**
   * Step 3: Login with API key + MFA code (subsequent logins)
   */
  async loginWithMfa(apiKey: string, code: string): Promise<VendorSessionResponse> {
    const url = getApiUrl(endpoint.vendor.auth.mfaLogin);

    const data = await handleApiCall<VendorSessionResponse>(async () => {
      return api.post({ url, body: { apiKey, code }, auth: false });
    });
    if (data?.token) {
      this.setSessionToken(data.token);
    }
    return data;
  }

  /**
   * Get dashboard data (requires session)
   */
  async getDashboard(): Promise<{
    vendor: Partial<LeadVendor>;
    stats: VendorStats;
    recentSubmissions: VendorSubmission[];
  }> {
    return this.makeAuthenticatedRequest('get', endpoint.vendor.dashboard);
  }

  /**
   * Get vendor profile (requires session)
   */
  async getProfile(): Promise<Partial<LeadVendor>> {
    return this.makeAuthenticatedRequest('get', endpoint.vendor.profile);
  }

  /**
   * Get submissions list (requires session)
   */
  async getSubmissions(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ submissions: VendorSubmission[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const urlPath = `${endpoint.vendor.submissions}${queryString ? `?${queryString}` : ""}`;

    return this.makeAuthenticatedRequest('get', urlPath);
  }

  /**
   * Resync a failed submission (requires session)
   */
  async resyncSubmission(submissionId: string): Promise<{
    external_id?: string;
    status: 'success' | 'failed' | 'no_match';
    lead_id?: string;
    agent_name?: string;
    error?: string;
    error_code?: string;
  }> {
    return this.makeAuthenticatedRequest('post', endpoint.vendor.resyncSubmission(submissionId));
  }

  /**
   * Resync all failed submissions (requires session)
   */
  async resyncAllFailed(): Promise<{
    total: number;
    succeeded: number;
    stillFailed: number;
  }> {
    return this.makeAuthenticatedRequest('post', endpoint.vendor.resyncAll);
  }

  /**
   * Get stats (requires session)
   */
  async getStats(): Promise<VendorStats> {
    return this.makeAuthenticatedRequest('get', endpoint.vendor.stats);
  }

  /**
   * Get API logs (requires session)
   */
  async getLogs(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ logs: VendorApiLog[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const urlPath = `${endpoint.vendor.logs}${queryString ? `?${queryString}` : ""}`;

    return this.makeAuthenticatedRequest('get', urlPath);
  }

  /**
   * Get top agents by lead count (requires session)
   */
  async getTopAgents(params?: {
    limit?: number;
  }): Promise<{ agents: TopAgent[] }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));

    const queryString = queryParams.toString();
    const urlPath = `${endpoint.vendor.topAgents}${queryString ? `?${queryString}` : ""}`;

    return this.makeAuthenticatedRequest('get', urlPath);
  }

  /**
   * Get batch submissions grouped by API request (requires session)
   */
  async getBatches(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ batches: BatchSubmission[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", String(params.limit));
    if (params?.offset) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const urlPath = `${endpoint.vendor.batches}${queryString ? `?${queryString}` : ""}`;

    return this.makeAuthenticatedRequest('get', urlPath);
  }

  /**
   * Resync all failed submissions in a batch (requires session)
   */
  async resyncBatch(batchId: string): Promise<{
    total: number;
    succeeded: number;
    stillFailed: number;
  }> {
    return this.makeAuthenticatedRequest('post', endpoint.vendor.resyncBatch(batchId));
  }

  /**
   * Get promotion rate limit status (requires session)
   */
  async getPromotionStatus(): Promise<{
    canSendNow: boolean;
    nextAllowedAt: string | null;
    lastSentAt: string | null;
  }> {
    return this.makeAuthenticatedRequest('get', endpoint.vendor.promotionStatus);
  }

  /**
   * Send promotional notification to all agents who received leads (requires session)
   */
  async sendPromotion(params: {
    title: string;
    message: string;
    linkUrl?: string;
  }): Promise<{
    notificationsSent: number;
    nextAvailableAt?: string;
  }> {
    return this.makeAuthenticatedRequest('post', endpoint.vendor.sendPromotion, params);
  }

  /**
   * Logout - clear session
   */
  logout() {
    this.clearSessionToken();
  }
}

// Export singleton instances
export const vendorAdminService = new VendorAdminService();
export const vendorPortalService = new VendorPortalService();

// Default export for backwards compatibility
export default {
  admin: vendorAdminService,
  portal: vendorPortalService,
};
